import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { assistantService } from '../services';
import { surveyService } from '../services/survey.service';
import { messageHandler } from '../services/message-handler.service';
import type { Assistant, AssistantFunction } from '../types/assistant';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import AssistantList from '../components/assistants/AssistantList';
import AssistantForm from '../components/assistants/AssistantForm';
import Modal from '../components/ui/Modal';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function Assistants() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingAssistant, setEditingAssistant] = useState<Assistant | null>(null);
  const [selectedRelatedAssistants, setSelectedRelatedAssistants] = useState<number[]>([]);
  const [selectedRelatedFunctions, setSelectedRelatedFunctions] = useState<number[]>([]);
  const queryClient = useQueryClient();
  const user = useAuthStore(state => state.user);

  // Queries
  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const { data: assistantTypes } = useQuery({
    queryKey: ['assistant-types'],
    queryFn: () => assistantService.getAssistantTypes(),
  });

  const { data: integrations } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => assistantService.getIntegrations(),
  });

  const { data: timeConditions } = useQuery({
    queryKey: ['time-conditions'],
    queryFn: () => assistantService.getTimeConditions(),
  });

  const { data: assistantFunctions } = useQuery({
    queryKey: ['assistant-functions'],
    queryFn: () => assistantService.getAssistantFunctions(),
  });

  const { data: assistantModels } = useQuery({
    queryKey: ['assistant-models'],
    queryFn: () => assistantService.getAssistantModels(),
  });

  const { data: surveys } = useQuery({
    queryKey: ['surveys'],
    queryFn: () => surveyService.list(),
  });

  const safeAssistants = Array.isArray(assistants) ? assistants : assistants?.data || [];
  const safeAssistantTypes = Array.isArray(assistantTypes) ? assistantTypes : assistantTypes?.data || [];
  const safeAssistantModels = Array.isArray(assistantModels) ? assistantModels : assistantModels?.data || [];
  const safeIntegrations = Array.isArray(integrations) ? integrations : integrations?.data || [];
  const safeTimeConditions = Array.isArray(timeConditions) ? timeConditions : timeConditions?.data || [];
  const safeAssistantFunctions = Array.isArray(assistantFunctions) ? assistantFunctions : assistantFunctions?.data || [];
  const safeSurveys = Array.isArray(surveys) ? surveys : surveys?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Assistant>) => assistantService.createAssistant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] });
      messageHandler.success('Assistente criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Assistant) => assistantService.updateAssistant(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] });
      messageHandler.success('Assistente atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => assistantService.deleteAssistant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] });
      messageHandler.success('Assistente excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (data: any) => {
    try {
      const processedData = {
        ...data,
        status_mode: data.status_mode,
        time_condition_id: data.time_condition_id ? Number(data.time_condition_id) : null,
        integration_id: data.integration_id ? Number(data.integration_id) : null,
        assistant_model_id: data.assistant_model_id ? Number(data.assistant_model_id) : null,
        related_assistant_ids: selectedRelatedAssistants,
        related_functions_id: selectedRelatedFunctions,
        trigger_keywords: data.trigger_keywords
          ? data.trigger_keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k)
          : [],
        trigger_duration: data.trigger_duration ? Number(data.trigger_duration) : undefined,
        trigger_models: data.trigger_models
          ? data.trigger_models.split('\n').map((m: string) => m.trim()).filter((m: string) => m)
          : [],
        voice_id: data.voice_id || undefined,
        generate_resume: data.generate_resume || false,
        close_ticket_at: data.close_ticket_at ? Number(data.close_ticket_at) : undefined,
        send_messages_only_ticketopen: data.send_messages_only_ticketopen || false,
        generate_resume_alerts: data.generate_resume_alerts || false,
        respond_on_first_group_message: data.respond_on_first_group_message || false,
        close_survey_id: data.close_survey_id ? Number(data.close_survey_id) : null,
        contact_name_update: data.contact_name_update || false,
      };

      if (editingAssistant) {
        updateMutation.mutate({
          ...editingAssistant,
          ...processedData,
        });
      } else {
        const name = data.name;
        const formattedName = `${user?.id}_${name.replace(/\s+/g, '_')}`;

        const assistantData = {
          ...processedData,
          name: formattedName,
          assistant_id: formattedName,
        };
        createMutation.mutate(assistantData);
      }
    } catch (error) {
      messageHandler.error(error);
    }
  };

  const handleDelete = (assistant: Assistant) => {
    messageHandler.confirm(
      'Excluir Assistente',
      'Tem certeza que deseja excluir este assistente?',
      () => {
        deleteMutation.mutate(assistant.id);
        closeModal();
      }
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingAssistant(null);
    setSelectedRelatedAssistants([]);
    setSelectedRelatedFunctions([]);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Assistentes"
          description="Lista de todos os assistentes em sua conta."
          actions={
            <Button
              onClick={() => {
                setEditingAssistant(null);
                setSelectedRelatedAssistants([]);
                setSelectedRelatedFunctions([]);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Assistente
            </Button>
          }
        />

        <AssistantList
          assistants={safeAssistants}
          onEdit={assistant => {
            setEditingAssistant(assistant);
            setSelectedRelatedAssistants(assistant.related_assistant_ids || []);
            setSelectedRelatedFunctions(assistant.related_functions_id || []);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
        />

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingAssistant ? 'Editar Assistente' : 'Novo Assistente'}
          size="xl"
        >
          <AssistantForm
            assistant={editingAssistant}
            assistantTypes={safeAssistantTypes}
            assistantModels={safeAssistantModels}
            integrations={safeIntegrations}
            timeConditions={safeTimeConditions}
            assistantFunctions={safeAssistantFunctions}
            allAssistants={safeAssistants}
            surveys={safeSurveys}
            selectedRelatedAssistants={selectedRelatedAssistants}
            selectedRelatedFunctions={selectedRelatedFunctions}
            onRelatedAssistantsChange={setSelectedRelatedAssistants}
            onRelatedFunctionsChange={setSelectedRelatedFunctions}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Assistants;
